import React, { useContext, useEffect, useState } from 'react';
import {Button, Icon, isMobileDevice} from '../../../patterns';
import { Emoticons, TextField } from '../forms';
import { TeamResultsStore } from '../../../app/src/dashboard/team-results-store';
import { IComment } from './feedback-answers';
import { StoreContext } from '../../../app/src/base/root-store';
import ReactDOM from 'react-dom';
import {translateComment} from '../../../app/src/questions/questionUtil';

interface FeedbackCommentThreadProps {
    answerId: string;
    comments: IComment[];
    store: TeamResultsStore;
    onCommentAdded?: (comment: IComment) => void;
    onCommentDeleted?: (commentId: string) => void;
}

export const FeedbackCommentThread = ({
    answerId,
    comments,
    store,
    onCommentAdded,
    onCommentDeleted,
}: FeedbackCommentThreadProps): JSX.Element => {
    const className = !isMobileDevice()
        ? 'feedback-answers__inline-comments feedback-answers__inline-comments--desktop'
        : 'feedback-answers__inline-comments';
    const userStore = useContext(StoreContext).userStore;
    const [localComments, setLocalComments] = useState<IComment[]>(comments);
    const [newReply, setNewReply] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editCommentId, setEditCommentId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');

    useEffect(() => {
        const fetchComments = async () => {
            try {
                await store.loadRepliesForQuestion(answerId);
                const updated = store.variables.questionWithComment?.comments ?? [];

                setLocalComments(updated);
            } catch (error) {
                console.error('Failed to load comments:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchComments();
    }, [answerId, store, store.translateCommentsEnabled]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setEditCommentId(null);
                setEditText('');
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleReplySubmit = async () => {
        if (!newReply.trim()) {
            return;
        }
        setSending(true);
        try {
            await store.addReplyToQuestionComment(answerId, newReply);
            setNewReply('');
            const updated = store.variables.questionWithComment?.comments ?? [];
            const newComment = updated[updated.length - 1];

            setLocalComments(updated);
            onCommentAdded?.(newComment);
        } catch (error) {
            console.error('Failed to send reply:', error);
        } finally {
            setSending(false);
        }
    };

    const handleEdit = (comment: IComment) => {
        setEditCommentId(comment.id);
        setEditText(comment.text);
    };

    const handleEditSubmit = async () => {
        if (!editCommentId || !editText.trim()) {
            return;
        }
        try {
            await store.editReplyToQuestionComment(editCommentId, editText);
            await store.loadRepliesForQuestion(answerId);
            const updated = store.variables.questionWithComment?.comments ?? [];

            setLocalComments(updated);
            setEditCommentId(null);
            setEditText('');
        } catch (error) {
            console.error('Failed to edit comment:', error);
        }
    };

    const handleDelete = async (commentId: string) => {
        try {
            await store.deleteReplyToQuestionComment(commentId);
            await store.loadRepliesForQuestion(answerId);
            const updated = store.variables.questionWithComment?.comments ?? [];

            setLocalComments(updated);
            onCommentDeleted?.(commentId);
            if (store.variables.questionWithComment?.id === answerId) {
                store.variables.questionWithComment.commentCount = updated.length;
            }
        } catch (error) {
            console.error('Failed to delete comment:', error);
        }
    };

    return (
        <div className={className}>
            {loading ? (
                <p>Loading replies…</p>
            ) : (
                localComments.map((comment) => {
                    const isEditing = editCommentId === comment.id;
                    const isMobile = isMobileDevice();

                    return (
                        <div
                            key={comment.id}
                            className={`comment-thread ${!isMobile ? 'comment-thread--desktop' : ''} ${isEditing ? 'comment-thread--editing' : ''}`}
                        >
                            <span className={!isMobile ? 'comment-reply__icon--desktop' : 'comment-reply__icon'}>
                                <Icon name="message" />
                            </span>
                            <div className={!isMobile ? 'comment-thread__text--desktop' : 'comment-thread__text'}>
                                {isEditing && !isMobile ? (
                                    <>
                                        <div className="comment-reply__edit-form">
                                            <TextField
                                                id={`edit-${comment.id}`}
                                                name=""
                                                label={store.label('questions.edit-question-button')}
                                                value={editText}
                                                onChange={setEditText}
                                                element="textarea"
                                                autoFocus
                                                inputClassName="textarea--scrollable textarea--large"
                                            />
                                            <div className="comment-reply__edit-actions">
                                                <Button
                                                    text={store.label('company.modal.save')}
                                                    onClick={handleEditSubmit}
                                                    disabled={!editText.trim()}
                                                    style="square"
                                                />
                                                <Button
                                                    text={store.label('company.modal.cancel')}
                                                    onClick={() => {
                                                        setEditCommentId(null);
                                                        setEditText('');
                                                    }}
                                                    style="square"
                                                    color="white"
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="comment-reply__content">
                                            <Emoticons
                                                text={
                                                    store.translateCommentsEnabled
                                                        ? translateComment(comment, userStore.payload?.language)
                                                        : comment.text
                                                }
                                            />
                                        </div>
                                        {userStore?.payload?.employeeId === comment.employeeId && (
                                            <div className={!isMobile ? 'comment-reply__actions--desktop' : 'comment-reply__actions'}>
                                                <span onClick={() => handleEdit(comment)}>{store.label('questions.edit-question-button')}</span>
                                                <span onClick={() => handleDelete(comment.id)}>{store.label('questions.delete-question-button')}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })
            )}

            {editCommentId && isMobileDevice() &&
                ReactDOM.createPortal(
                    <div className="edit-overlay">
                        <div className="edit-popup">
                            <TextField
                                id={`edit-${editCommentId}`}
                                name=""
                                label={store.label('questions.edit-question-button')}
                                value={editText}
                                onChange={setEditText}
                                element="textarea"
                                autoFocus
                                className="edit-popup__textarea edit-popup__textarea--tall"
                                inputClassName="textarea--scrollable textarea--large"
                            />
                            <div className="edit-popup__actions">
                                <Button
                                    text={store.label('company.modal.save')}
                                    onClick={handleEditSubmit}
                                    disabled={!editText.trim()}
                                    style="square"
                                />
                                <Button
                                    text={store.label('company.modal.cancel')}
                                    onClick={() => {
                                        setEditCommentId(null);
                                        setEditText('');
                                    }}
                                    style="square"
                                    color="white"
                                />
                            </div>
                        </div>
                    </div>,
                    document.body!,
                )
            }

            {!editCommentId && (
                <div className="comment-form">
                    <TextField
                        id={`reply-${answerId}`}
                        name={store.label('feedback.comment.add-comment')}
                        label={store.label('feedback.comment.add-comment')}
                        focusLabel={store.label('feedback.comment.add-comment')}
                        element="textarea"
                        value={newReply}
                        onChange={setNewReply}
                        disabled={sending}
                        inputClassName="textarea--scrollable textarea--large"
                    />
                    <Button
                        text={store.label('chat.submit')}
                        onClick={handleReplySubmit}
                        disabled={sending || !newReply.trim()}
                    />
                </div>
            )}
        </div>
    );
};
